import React, { type ReactNode, type SyntheticEvent } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import ChevronLeftLargeIcon from '@atlaskit/icon/glyph/chevron-left-large';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import Modal, { ModalHeader, ModalTitle, ModalBody, ModalFooter } from '@atlaskit/modal-dialog';
import Pagination from '@atlaskit/pagination';
import { Box, xcss } from '@atlaskit/primitives';
import SectionMessage from '@atlaskit/section-message';
import Spinner from '@atlaskit/spinner';
import { fontFallback } from '@atlaskit/theme/typography';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import type { Page, Priority, PriorityMappings } from '../../../common/ui/types';
import { calculateRowsOnPage } from '../../../common/utils';
import { MappingList } from './mapping-list';
import messages from './messages';

export type MappingModalProps = {
	isError: boolean;
	/**
	 * To disable the buttons on submit
	 */
	isDisabled: boolean;
	isLoading: boolean;
	/**
	 * Should be either:
	 * Map issues in selected projects | Map issues in removed projects
	 */
	modalTitle: string;
	/**
	 * Should be respective description for either:
	 * Map issues in selected projects | Map issues in removed projects
	 */
	modalDescription: string | ReactNode;
	/**
	 * Represent the current paginated page of priorities that need to be mapped.
	 */
	page: Page<Priority>;
	/**
	 * A record of priority mappings for the current scheme.
	 */
	mappings: PriorityMappings;
	/**
	 * Represents the priorities that can be selected or mapped to. This is used to
	 * render the dropdown menu options.
	 */
	mappablePriorities: Priority[];
	/**
	 * Updates the mappings record
	 * @param currentPriorityId
	 * @param newPriority
	 */
	onUpdateMapping: (currentPriorityId: string, newPriority: Priority) => void;
	/**
	 * Callback to handle modal pagination
	 * @param event
	 * @param page
	 */
	onFetch: (event: SyntheticEvent<Element, Event>, page: number) => void;
	/**
	 * Renders the primary button for mapping modal, will either transition to next modal if required
	 * or display the create or edit scheme button.
	 */
	renderPrimaryButton: () => JSX.Element;
	/**
	 * Callback for link button of the modal, typically will be to return to previous modal
	 */
	onPreviousStep: () => void;
};

export const MappingModalView = ({
	isError,
	isDisabled,
	isLoading,
	modalTitle,
	modalDescription,
	mappings,
	mappablePriorities,
	onUpdateMapping,
	page,
	onFetch,
	onPreviousStep,
	renderPrimaryButton,
}: MappingModalProps) => {
	const { formatMessage } = useIntl();
	const { currentPage, items, totalItems, pageSize } = page;

	const { startRow, endRow } = calculateRowsOnPage({
		rowsPerPage: pageSize,
		selectedPage: currentPage,
		totalRows: totalItems,
	});

	const renderMappingListContent = (): JSX.Element => {
		if (isError) {
			return (
				<SectionMessage appearance="error">
					{formatMessage(messages.mappingModalUnexpectedErrorTitle)}
				</SectionMessage>
			);
		}

		if (isLoading) {
			return (
				<Box xcss={loadingMappingListContainerStyles}>
					<Spinner size="xlarge" />
				</Box>
			);
		}

		return (
			<MappingList
				priorities={items}
				mappablePriorities={mappablePriorities}
				onUpdateMapping={onUpdateMapping}
				mappings={mappings}
			/>
		);
	};

	return (
		<Modal>
			<ModalHeader>
				<MappingTitleContainer>
					<Button
						isDisabled={isDisabled}
						iconBefore={
							<ChevronLeftLargeIcon
								label={formatMessage(messages.mappingModalReturnModalButtonLabel)}
							/>
						}
						onClick={onPreviousStep}
					/>
					<ModalTitle>{modalTitle}</ModalTitle>
				</MappingTitleContainer>
			</ModalHeader>
			<ModalBody>
				{modalDescription}
				<Box
					padding="space.200"
					backgroundColor="elevation.surface.sunken"
					xcss={mappingTableBoxContainerStyles}
				>
					{renderMappingListContent()}
				</Box>
				{!isError && totalItems && totalItems > pageSize && (
					<MappingTablePaginationContainer>
						<Pagination
							nextLabel={formatMessage(messages.mappingModalPaginationNextButtonLabel)}
							label={formatMessage(messages.mappingModalPaginationWrapperLabel)}
							pageLabel={formatMessage(messages.mappingModalPaginationPageLabel)}
							// Creates a 1 indexed array to represent that amount of pages we are paginating
							pages={Array.from({ length: Math.ceil(totalItems / pageSize) }, (_, i) => i + 1)}
							previousLabel={formatMessage(messages.mappingModalPaginationPreviousButtonLabel)}
							onChange={onFetch}
							// This is not 1 indexed .-.
							selectedIndex={page.currentPage - 1}
						/>
						<MappingTablePaginationSummary>
							{formatMessage(messages.mappingModalPaginatorSummary, {
								startRow,
								endRow,
								totalRows: totalItems,
							})}
						</MappingTablePaginationSummary>
					</MappingTablePaginationContainer>
				)}
			</ModalBody>
			<ModalFooter>
				<Button appearance="link" onClick={onPreviousStep} isDisabled={isDisabled}>
					{formatMessage(messages.mappingModalFooterBack)}
				</Button>
				{renderPrimaryButton()}
			</ModalFooter>
		</Modal>
	);
};

const mappingTableBoxContainerStyles = xcss({
	borderColor: 'color.border',
	borderStyle: 'solid',
	borderRadius: 'border.radius',
	borderWidth: 'border.width',
	marginTop: 'space.150',
	/**
	 * This height is the height of the mapping list box with the max amount of rows which is 8,
	 * 586 = 554 (div height) + 16 (padding top) + 16 (padding top)
	 */
	minHeight: '586px',
});

const loadingMappingListContainerStyles = xcss({
	display: 'center',
	justifyContent: 'center',
	alignItems: 'center',
	/**
	 * This height is the height of the mapping list box with the max amount of rows which is 8,
	 * this div does not include the padding so = 554.
	 */
	minHeight: '554px',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MappingTitleContainer = styled.div({
	display: 'flex',
	gap: token('space.200', '12px'),
	alignItems: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MappingTablePaginationSummary = styled.p({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- PLEASE FIX - ENABLING FLAT LINT CONFIG
	font: token('font.body.small', fontFallback.body.small),
	textAlign: 'center',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const MappingTablePaginationContainer = styled.div({
	display: 'flex',
	justifyContent: 'center',
	flexDirection: 'column',
	alignItems: 'center',
	marginTop: token('space.150', '12px'),
});
